import { ErrorMessage, Field, Form, Formik } from 'formik';
import React, { useEffect, useRef, useState } from 'react';
import {
  FiChevronDown,
  FiChevronLeft,
  FiX
} from 'react-icons/fi';
import { useDispatch, useSelector } from 'react-redux';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import { toast, ToastContainer } from 'react-toastify';
import * as Yup from 'yup';
import { RootState } from '../../app/store';
import { updateSegment } from '../../features/audience/audienceSlice';
import { BASE_URL } from '../../utils/constants';
import { getAuthContext } from '../../utils/jwt';

const FiChevronLeftComponent = FiChevronLeft as unknown as React.ComponentType<{ size?: number | string; className?: string; }>;
const FiChevronDownComponent = FiChevronDown as unknown as React.ComponentType<{ size?: number | string; className?: string; }>;
const FiXComponent = FiX as unknown as React.ComponentType<{ size?: number | string; className?: string; }>;

interface FormValues {
  segmentName: string;
  logicalOperator: 'AND' | 'OR';
  selectedFields: string[];
  fieldTypes: { [key: string]: string };
  fieldOperators: { [key: string]: string };
  fieldValues: { [key: string]: string | number };
}

interface ExistingSegment {
  id: string;
  name: string;
  type: string;
  description?: string | null;
  rulesJson: any[];
}

// ==================== OPERATOR OPTIONS ====================
const OPERATOR_OPTIONS = [
  'EQUALS',
  'NOT_EQUALS',
  'GT',
  'LT',
  'GTE',
  'LTE',
  'IN',
  'IS_NOT_NULL',
  'MATCH_ALL',
  'NOT_IN',
  'EXISTS',
  'BETWEEN',
  'CONTAINS'
];

// Operators that don't require a value
const OPERATORS_WITHOUT_VALUE = ['IS_NOT_NULL', 'EXISTS'];

const DISPLAY_FIELDS = ['Name', 'Email', 'Contact', 'Gender', 'DOB', 'Location'];

const mapToDisplay = (apiName: string): string => {
  const map: { [key: string]: string } = {
    name: 'Name', email: 'Email', phone: 'Contact',
    gender: 'Gender', dob: 'DOB', location: 'Location'
  };
  return map[apiName.toLowerCase()] || apiName;
};

const mapToApi = (displayName: string): string => {
  const map: { [key: string]: string } = {
    Name: 'name', Email: 'email', Contact: 'phone',
    Gender: 'gender', DOB: 'dob', Location: 'location'
  };
  return map[displayName] || displayName.toLowerCase();
};

// ==================== MAP INPUT TYPE TO VALUE ====================
const getRuleValueFromInputType = (inputType: string): string => {
  switch (inputType) {
    case 'Number':
      return 'Number';
    case 'Calendar':
      return 'Calendar';
    case 'Dropdown':
      return 'Dropdown';
    case 'Text':
    default:
      return 'Text';
  }
};

// ==================== EXTRACT INPUT TYPE FROM RULE VALUE ====================
const getInputTypeFromRuleValue = (value: string): string => {
  const knownTypes = ['Text', 'Number', 'Calendar', 'Dropdown'];
  if (knownTypes.includes(value)) {
    return value;
  }
  return 'Text';
};

const EditSegment: React.FC = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { segment } = useParams<{ segment: string }>();
  const { state } = useLocation();
  const segments = useSelector((state: RootState) => state.audience.segments);

  const decodedSegmentName = segment ? segment.replace(/-/g, ' ') : '';
  const currentSegment = state?.segmentData || segments.find((s) => s.name === decodedSegmentName);

  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const [newFieldName, setNewFieldName] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [existingSegments, setExistingSegments] = useState<ExistingSegment[]>([]);
  const dropdownRef = useRef<HTMLDivElement>(null);

  const isDynamic = currentSegment?.type === 'DYNAMIC';

  const fetchExistingSegments = async () => {
    const { token, tenantId, userId } = getAuthContext();
    if (!token) return;

    try {
      const response = await fetch(`${BASE_URL}/v1/audience-insights/segments`, {
        method: 'GET',
        headers: {
          'X-Tenant-Id': tenantId,
          'X-User-Id': userId || '',
          'Authorization': `Bearer ${token}`,
        },
      });

      if (!response.ok) throw new Error(`HTTP ${response.status}`);

      const data = await response.json();
      const mappedSegments = (data.content || []).map((seg: any) => ({
        id: seg.id,
        name: seg.name,
        type: seg.type,
        description: seg.description,
        rulesJson: seg.rulesJson || [],
      }));

      setExistingSegments(mappedSegments);
    } catch (error) {
      // console.error('Failed to fetch existing segments:', error);
      toast.error('Failed to fetch existing segments. Please try again later.');
    }
  };

  useEffect(() => {
    fetchExistingSegments();
  }, []);

  const getFieldTypeOptions = (field: string) => {
    if (field === 'DOB') return ['Calendar', 'Text'];
    if (field === 'Gender') return ['Dropdown', 'Text'];
    return ['Text', 'Number'];
  };

  const extractFieldsFromRules = (rulesJson: any[]) => {
    if (!rulesJson?.length) return {
      fields: [],
      fieldTypes: {},
      fieldOperators: {},
      fieldValues: {},
      logicalOperator: 'AND'
    };

    const fields = new Set<string>();
    const fieldTypes: { [key: string]: string } = {};
    const fieldOperators: { [key: string]: string } = {};
    const fieldValues: { [key: string]: string | number } = {};
    let logicalOperator = 'AND';

    rulesJson.forEach(group => {
      logicalOperator = group.type || 'AND';
      group.rules?.forEach((rule: any) => {
        if (rule.field) {
          const displayName = mapToDisplay(rule.field);
          fields.add(displayName);
          fieldOperators[displayName] = rule.operator || 'EQUALS';
          fieldValues[displayName] = rule.value || '';
          if (rule.value) {
            fieldTypes[displayName] = getInputTypeFromRuleValue(rule.value);
          } else {
            fieldTypes[displayName] = getFieldTypeOptions(displayName)[0];
          }
        }
      });
    });

    return {
      fields: Array.from(fields),
      fieldTypes,
      fieldOperators,
      fieldValues,
      logicalOperator
    };
  };

  let initialSelected: string[] = [];
  let initialFieldTypes: { [key: string]: string } = {};
  let initialFieldOperators: { [key: string]: string } = {};
  let initialFieldValues: { [key: string]: string | number } = {};
  let initialLogicalOperator: 'AND' | 'OR' = 'AND';

  if (currentSegment?.rulesJson) {
    const extracted = extractFieldsFromRules(currentSegment.rulesJson);
    initialSelected = extracted.fields;
    initialFieldTypes = extracted.fieldTypes;
    initialFieldOperators = extracted.fieldOperators;
    initialFieldValues = extracted.fieldValues;
    initialLogicalOperator = extracted.logicalOperator as 'AND' | 'OR';
  }

  const initialValues: FormValues = {
    segmentName: currentSegment?.name || '',
    logicalOperator: initialLogicalOperator,
    selectedFields: initialSelected,
    fieldTypes: initialFieldTypes,
    fieldOperators: initialFieldOperators,
    fieldValues: initialFieldValues,
  };

  const originalValuesRef = useRef<FormValues>({
    segmentName: initialValues.segmentName,
    logicalOperator: initialValues.logicalOperator,
    selectedFields: [...initialValues.selectedFields],
    fieldTypes: { ...initialValues.fieldTypes },
    fieldOperators: { ...initialValues.fieldOperators },
    fieldValues: { ...initialValues.fieldValues }
  });

  useEffect(() => {
    if (currentSegment) {
      originalValuesRef.current = {
        segmentName: currentSegment.name || '',
        logicalOperator: initialLogicalOperator,
        selectedFields: [...initialSelected],
        fieldTypes: { ...initialFieldTypes },
        fieldOperators: { ...initialFieldOperators },
        fieldValues: { ...initialFieldValues }
      };
    }
  }, [currentSegment]);

  const buildRulesJson = (
    selectedFields: string[],
    fieldTypes: { [key: string]: string },
    fieldOperators: { [key: string]: string },
    fieldValues: { [key: string]: string | number },
    logicalOperator: string
  ) => {
    if (selectedFields.length === 0) return [];

    const rules = selectedFields.map((field) => {
      let apiField = mapToApi(field);
      const operator = fieldOperators[field] || 'EQUALS';
      let value = fieldValues[field] || '';

      if (!value && !OPERATORS_WITHOUT_VALUE.includes(operator)) {
        value = getRuleValueFromInputType(fieldTypes[field] || 'Text');
      }

      return {
        type: "ATTRIBUTE",
        field: apiField,
        operator: operator,
        value: String(value)
      };
    });

    return [
      {
        type: logicalOperator,
        rules: rules
      }
    ];
  };

  const updateSegmentAPI = async (segmentId: string, values: FormValues) => {
    const { token, tenantId, userId } = getAuthContext();
    if (!token || !segmentId) {
      toast.error('Authentication or Segment ID missing');
      return false;
    }

    const url = `${BASE_URL}/v1/audience-insights/segments/${segmentId}`;

    let payload: any = {
      name: values.segmentName.trim(),
      description: currentSegment?.description || `${values.segmentName.trim()} segment`,
      type: isDynamic ? "DYNAMIC" : "STATIC",
    };

    if (isDynamic) {
      payload.rulesJson = buildRulesJson(
        values.selectedFields,
        values.fieldTypes,
        values.fieldOperators,
        values.fieldValues,
        values.logicalOperator
      );
    }


    try {
      const response = await fetch(url, {
        method: 'PATCH',
        headers: {
          'X-Tenant-Id': tenantId,
          'X-User-Id': userId || '',
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(payload),
      });

      if (!response.ok) throw new Error(`Failed: ${response.status}`);

      const data = await response.json();

      dispatch(updateSegment({
        name: values.segmentName.trim(),
        fields: values.selectedFields,
        fieldTypes: values.fieldTypes,
      }));

      toast.success('Segment updated successfully!');
      return true;
    } catch (error) {
      // console.error(error);
      toast.error('Failed to update segment');
      return false;
    }
  };

  const handleAddNewField = (setFieldValue: any, values: FormValues) => {
    if (!newFieldName.trim() || !isDynamic) return;
    const field = newFieldName.trim();
    if (!values.selectedFields.includes(field)) {
      setFieldValue('selectedFields', [...values.selectedFields, field]);
      setFieldValue('fieldTypes', { ...values.fieldTypes, [field]: getFieldTypeOptions(field)[0] });
      setFieldValue('fieldOperators', { ...values.fieldOperators, [field]: 'EQUALS' });
      setFieldValue('fieldValues', {
        ...values.fieldValues,
        [field]: field === 'Gender' ? 'Male' : ''
      });
    }
    setNewFieldName('');
  };

  const handleDeleteField = (setFieldValue: any, values: FormValues, field: string) => {
    setFieldValue('selectedFields', values.selectedFields.filter((f: string) => f !== field));
    const newTypes = { ...values.fieldTypes };
    delete newTypes[field];
    setFieldValue('fieldTypes', newTypes);

    const newOperators = { ...values.fieldOperators };
    delete newOperators[field];
    setFieldValue('fieldOperators', newOperators);

    const newValues = { ...values.fieldValues };
    delete newValues[field];
    setFieldValue('fieldValues', newValues);
  };

  useEffect(() => {
    const handler = (e: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target as Node)) setIsDropdownOpen(false);
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  if (!currentSegment) return <div>Segment not found...</div>;

  const validationSchema = Yup.object({
    segmentName: Yup.string()
      .required('Segment name is required')
      .min(2, 'Segment name must be at least 2 characters')
      .test('unique-name', 'Name unavailable, please choose a different name', function (value) {
        if (!value) return true;
        const nameExists = existingSegments.some(
          segment =>
            segment.name.toLowerCase() === value.trim().toLowerCase() &&
            segment.id !== currentSegment?.id
        );
        if (nameExists) {
          return this.createError({ message: 'Name unavailable, please choose a different name' });
        }
        return true;
      }),
    selectedFields: isDynamic
      ? Yup.array().min(1, 'Select at least one field')
      : Yup.array().notRequired(),
    logicalOperator: isDynamic
      ? Yup.string().required('Logical operator is required')
      : Yup.string().notRequired(),
  });

  // Render value field based on field type — rounded-full to match pill style
  const renderValueField = (field: string, fieldValue: string | number, requiresValue: boolean) => {
    if (!requiresValue) {
      return (
        <Field
          name={`fieldValues.${field}`}
          placeholder="Not required"
          disabled={true}
          className="w-full border border-gray-300 rounded-full px-4 py-2 text-sm bg-gray-100 text-gray-500 focus:outline-none"
        />
      );
    }

    if (field === 'Gender') {
      return (
        <Field
          as="select"
          name={`fieldValues.${field}`}
          className="w-full border border-gray-300 rounded-full px-4 py-2 text-sm text-[#3F5265] bg-white shadow-sm focus:outline-none focus:ring-2 focus:ring-teal-500"
        >
          <option value="Male">Male</option>
          <option value="Female">Female</option>
        </Field>
      );
    }

    if (field === 'DOB') {
      return (
        <Field
          type="date"
          name={`fieldValues.${field}`}
          className="w-full border border-gray-300 rounded-full px-4 py-2 text-sm text-[#3F5265] bg-white shadow-sm focus:outline-none focus:ring-2 focus:ring-teal-500"
        />
      );
    }

    if (field === 'Contact') {
      return (
        <Field
          type="number"
          name={`fieldValues.${field}`}
          placeholder="Enter phone number *"
          className="w-full border border-gray-300 rounded-full px-4 py-2 text-sm text-[#3F5265] shadow-sm focus:outline-none focus:ring-2 focus:ring-teal-500"
        />
      );
    }

    return (
      <Field
        name={`fieldValues.${field}`}
        placeholder="Enter value *"
        className="w-full border border-gray-300 rounded-full px-4 py-2 text-sm text-[#3F5265] shadow-sm focus:outline-none focus:ring-2 focus:ring-teal-500"
      />
    );
  };

  return (
    <div className="p-2">
      <button onClick={() => navigate('/segments')} className="flex items-center text-teal-600 mb-4 hover:underline">
        <FiChevronLeftComponent className="mr-1" /> Back to Segments
      </button>

      <h2 className="text-2xl font-bold text-[#3F5265]">Edit Segment</h2>
      <p className="text-[#657584]">Edit existing segment by updating name and fields.</p>

      <div className="mt-2 mb-4">
        <span className={`inline-flex px-3 py-1 rounded-full text-sm font-medium ${isDynamic ? 'bg-blue-100 text-blue-800' : 'bg-gray-100 text-gray-800'}`}>
          {isDynamic ? 'Dynamic Segment' : 'Static Segment'}
        </span>
      </div>

      <Formik
        initialValues={initialValues}
        validationSchema={validationSchema}
        enableReinitialize
        onSubmit={async (values) => {
          setIsSubmitting(true);
          const success = await updateSegmentAPI(currentSegment.id, values);
          if (success) {
            setTimeout(() => navigate('/segments'), 1500);
          }
          setIsSubmitting(false);
        }}
      >
        {({ values, setFieldValue, isValid, touched, errors }) => {
          const hasChanges = () => {
            const orig = originalValuesRef.current;

            if (values.segmentName.trim() !== orig.segmentName.trim()) return true;
            if (values.logicalOperator !== orig.logicalOperator) return true;

            const currentFields = [...values.selectedFields].sort();
            const originalFields = [...orig.selectedFields].sort();
            if (JSON.stringify(currentFields) !== JSON.stringify(originalFields)) return true;

            for (const field of values.selectedFields) {
              if (values.fieldTypes[field] !== orig.fieldTypes[field]) return true;
              if (values.fieldOperators[field] !== orig.fieldOperators[field]) return true;
              if (String(values.fieldValues[field]) !== String(orig.fieldValues[field])) return true;
            }
            return false;
          };

          const areAllValuesFilled = values.selectedFields.every(
            field => {
              const operator = values.fieldOperators[field] || 'EQUALS';
              if (OPERATORS_WITHOUT_VALUE.includes(operator)) {
                return true;
              }
              const value = values.fieldValues[field];
              return value !== undefined && value !== null && String(value).trim() !== '';
            }
          );

          const isFormValid = isDynamic
            ? values.segmentName.trim() !== '' &&
            !errors.segmentName &&
            values.selectedFields.length > 0 &&
            areAllValuesFilled
            : values.segmentName.trim() !== '' && !errors.segmentName;

          const isButtonEnabled = isFormValid && hasChanges() && !isSubmitting;

          const hasFieldValueErrors = values.selectedFields.some(field => {
            const operator = values.fieldOperators[field] || 'EQUALS';
            if (OPERATORS_WITHOUT_VALUE.includes(operator)) return false;
            const value = values.fieldValues[field];
            return value === undefined || value === null || String(value).trim() === '';
          });

          return (
            <Form className="bg-white p-6 rounded-lg shadow-sm border border-gray-200 mt-6">
              {/* Top row: Segment Name + Logical Operator */}
              <div className="grid grid-cols-2 gap-4 mb-6">
                <div>
                  <label className="block mb-2 font-semibold text-[#3F5265] text-sm">SEGMENT NAME</label>
                  <Field
                    name="segmentName"
                    className={`w-full border px-3 py-2 rounded focus:ring-2 focus:ring-teal-500 ${errors.segmentName && touched.segmentName ? 'border-red-500' : 'border-gray-300'
                      }`}
                  />
                  <ErrorMessage name="segmentName" component="div" className="text-red-500 text-xs mt-1" />
                  {!errors.segmentName && values.segmentName && existingSegments.some(
                    s => s.name.toLowerCase() === values.segmentName.trim().toLowerCase() &&
                      s.id !== currentSegment?.id
                  ) && (
                      <div className='text-red-500 text-xs mt-1'>
                        Name unavailable, please choose a different name
                      </div>
                    )}
                </div>

                {isDynamic ? (
                  <div>
                    <label className="block mb-2 font-semibold text-[#3F5265] text-sm">LOGICAL OPERATOR</label>
                    <Field
                      as="select"
                      name="logicalOperator"
                      className="w-full border border-gray-300 px-3 py-2 rounded focus:ring-2 focus:ring-teal-500"
                    >
                      <option value="AND">AND</option>
                      <option value="OR">OR</option>
                    </Field>
                    <ErrorMessage name="logicalOperator" component="div" className="text-red-500 text-xs mt-1" />
                  </div>
                ) : (
                  <div>
                    <label className="block mb-2 font-semibold text-[#3F5265] text-sm">SELECT FILTERS</label>
                    <div className="w-full border border-gray-300 px-3 py-2 rounded bg-gray-100 cursor-not-allowed">
                      <span className="text-gray-400">Not applicable for static segments</span>
                    </div>
                  </div>
                )}
              </div>

              {/* SELECT FIELDS — full-width row below, only for Dynamic */}
              {isDynamic && (
                <div className="relative mb-6" ref={dropdownRef}>
                  <label className="block mb-2 font-semibold text-[#3F5265] text-sm">SELECT FILTERS</label>

                  {/* Trigger: chips + chevron */}
                  <div
                    onClick={() => setIsDropdownOpen(!isDropdownOpen)}
                    className="w-full min-h-[46px] border border-gray-300 px-3 py-2 rounded cursor-pointer bg-gray-50 flex items-center justify-between gap-2 flex-wrap"
                  >
                    <div className="flex flex-wrap gap-2 items-center flex-1">
                      {values.selectedFields.length === 0 ? (
                        <span className="text-gray-400 text-sm">Select filters</span>
                      ) : (
                        values.selectedFields.map((field) => (
                          <span
                            key={field}
                            className="inline-flex items-center gap-2 bg-[#E8EFF5] text-[#3F5265] text-sm font-medium rounded-full pl-4 pr-2 py-1.5"
                          >
                            {field}
                            <button
                              type="button"
                              onClick={(e) => {
                                e.stopPropagation();
                                handleDeleteField(setFieldValue, values, field);
                              }}
                              className="text-[#3F5265] hover:text-red-500 rounded-full p-0.5"
                              aria-label={`Remove ${field}`}
                            >
                              <FiXComponent size={14} />
                            </button>
                          </span>
                        ))
                      )}
                    </div>

                    <FiChevronDownComponent
                      className={`text-gray-500 transition-transform shrink-0 ${isDropdownOpen ? 'rotate-180' : ''}`}
                    />
                  </div>

                  {isDropdownOpen && (
                    <div className="absolute mt-1 bg-white border rounded shadow-md p-4 w-1/2 z-10">
                      <div className="mb-3">
                        <input
                          value={newFieldName}
                          onChange={e => setNewFieldName(e.target.value)}
                          placeholder="New field"
                          className="w-full border px-3 py-2 rounded mb-2 focus:ring-2 focus:ring-teal-500"
                        />
                        <button
                          type="button"
                          onClick={() => handleAddNewField(setFieldValue, values)}
                          className="w-full px-4 py-2 bg-teal-600 text-white rounded hover:bg-teal-700 disabled:bg-gray-300 disabled:text-gray-500 disabled:cursor-not-allowed"
                          disabled={!newFieldName.trim()}
                        >
                          + Add New Field
                        </button>
                      </div>

                      <div className="border-t pt-3">
                        <p className="text-sm text-gray-500 mb-2">Available Fields:</p>
                        {DISPLAY_FIELDS.map(field => (
                          <label key={field} className="flex items-center py-1 cursor-pointer hover:bg-gray-50 px-2 rounded">
                            <input
                              type="checkbox"
                              checked={values.selectedFields.includes(field)}
                              onChange={e => {
                                if (e.target.checked) {
                                  if (!values.selectedFields.includes(field)) {
                                    setFieldValue('selectedFields', [...values.selectedFields, field]);
                                    if (!values.fieldTypes[field]) {
                                      setFieldValue('fieldTypes', {
                                        ...values.fieldTypes,
                                        [field]: getFieldTypeOptions(field)[0]
                                      });
                                    }
                                    if (!values.fieldOperators[field]) {
                                      setFieldValue('fieldOperators', {
                                        ...values.fieldOperators,
                                        [field]: 'EQUALS'
                                      });
                                    }
                                    if (!values.fieldValues[field]) {
                                      setFieldValue('fieldValues', {
                                        ...values.fieldValues,
                                        [field]: field === 'Gender' ? 'Male' : ''
                                      });
                                    }
                                  }
                                } else {
                                  handleDeleteField(setFieldValue, values, field);
                                }
                              }}
                              className="mr-2 w-4 h-4 text-teal-600 border-gray-300 rounded"
                            />
                            {field}
                          </label>
                        ))}
                      </div>
                    </div>
                  )}
                  <ErrorMessage name="selectedFields" component="div" className="text-red-500 text-xs mt-1" />
                </div>
              )}

              {/* Static segment info message */}
              {!isDynamic && (
                <div className='mb-6 p-4 bg-blue-50 border border-blue-200 rounded-md'>
                  <p className='text-blue-700 text-sm'>
                    <strong>Static Segment:</strong> This segment type doesn't require any rules or field configurations.
                    You can manually add users to this segment later.
                  </p>
                </div>
              )}

              {/* Rule rows - compact pills */}
              {isDynamic && values.selectedFields.length > 0 && (
                <div className="mb-6 space-y-3">
                  {values.selectedFields.map(field => {
                    const operator = values.fieldOperators[field] || 'EQUALS';
                    const requiresValue = !OPERATORS_WITHOUT_VALUE.includes(operator);
                    const value = values.fieldValues[field];
                    const hasValueError = requiresValue && (value === undefined || value === null || String(value).trim() === '');

                    return (
                      <div
                        key={field}
                        className='flex items-start gap-3 flex-wrap bg-[#F4FEFF] border border-[#E0F8FB] rounded-[10px] px-4 py-3'
                      >
                        {/* Field name pill */}
                        <div className='relative'>
                          <select
                            value={field}
                            disabled
                            className='appearance-none bg-white border border-gray-300 rounded-full pl-4 pr-4 py-2 text-sm font-medium text-[#3F5265] shadow-sm focus:outline-none'
                          >
                            <option>{field}</option>
                          </select>
                          {/* <FiChevronDownComponent
                            size={14}
                            className='pointer-events-none absolute right-3 top-1/2 -translate-y-1/2 text-gray-500'
                          /> */}
                        </div>

                        {/* Operator pill */}
                        <div className='relative'>
                          <Field
                            as="select"
                            name={`fieldOperators.${field}`}
                            className='appearance-none bg-white border border-gray-300 rounded-full pl-4 pr-9 py-2 text-sm font-medium text-[#3F5265] shadow-sm focus:outline-none focus:ring-2 focus:ring-teal-500 cursor-pointer'
                            onChange={(e: any) => {
                              const newOperator = e.target.value;
                              setFieldValue(`fieldOperators.${field}`, newOperator);
                              if (OPERATORS_WITHOUT_VALUE.includes(newOperator)) {
                                setFieldValue(`fieldValues.${field}`, '');
                              }
                            }}
                          >
                            {OPERATOR_OPTIONS.map(op => (
                              <option key={op} value={op}>{op}</option>
                            ))}
                          </Field>
                          <FiChevronDownComponent
                            size={14}
                            className='pointer-events-none absolute right-3 top-1/2 -translate-y-1/2 text-gray-500'
                          />
                        </div>

                        {/* Value group */}
                        <div className='flex items-center flex-1 min-w-[260px]'>
                          <div className='flex-1'>
                            {renderValueField(field, values.fieldValues[field], requiresValue)}
                          </div>
                        </div>

                        {/* Remove button */}
                        <button
                          type="button"
                          onClick={() => handleDeleteField(setFieldValue, values, field)}
                          className='text-red-400 hover:text-red-600 mt-2'
                        >
                          <FiXComponent size={18} />
                        </button>

                        {hasValueError && (
                          <div className='w-full text-red-500 text-xs mt-1 pl-1'>
                            Value is required
                          </div>
                        )}
                      </div>
                    );
                  })}

                  {hasFieldValueErrors && (
                    <div className='text-red-500 text-xs mt-2'>
                      Please enter a value for all fields that require it
                    </div>
                  )}
                </div>
              )}

              <div className="flex justify-end gap-3">
                <button
                  type="button"
                  onClick={() => navigate('/segments')}
                  className="px-6 py-2 bg-orange-500 text-white rounded hover:bg-orange-600"
                  disabled={isSubmitting}
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={!isButtonEnabled}
                  className={`px-6 py-2 rounded text-white font-medium ${isButtonEnabled ? 'bg-teal-600 hover:bg-teal-700' : 'bg-gray-300 cursor-not-allowed'
                    }`}
                >
                  {isSubmitting ? 'Updating...' : 'Update'}
                </button>
              </div>
            </Form>
          );
        }}
      </Formik>
      <ToastContainer />
    </div>
  );
};

export default EditSegment;